// The body for the exercise library (components/exercises/BodyFigure.jsx): a front and a back figure drawn as muscle
// groups, each tied to a body part (utils/exerciseRegions.js) and to the catalog's muscle group. The left half is drawn
// once and mirrored; viewBox 0 0 160 360

export const BASE = {
  head: { cx: 80, cy: 23, rx: 13.5, ry: 17.5 },
  neck: 'M72 34h16l1.2 20h-18.4z',
  // Torso, arm, leg, hand and foot (left half; mirrored). The arm and the hand hang a little away from the body
  half: [
    ['M80 51 L72 51 C66 55.5 58 58.5 52 60 C47.5 62 46 66 46.5 72 C46.5 80 46 88 47 95 C49 111 51 127 52 148 C50.5 160 47.5 168 47 176 C46.4 184 46.6 190 48 196 L80 198 Z'],
    ['M55 58.5 C44 57.5 35 62.5 32.6 72 C30.8 82 32.6 92 32.8 100 C30.2 110 29 120 28 130 C25 146 22 162 20 180 L29.4 182 C32.4 166 36 150 40 136 C43 124 45 112 46.2 100 C47 92 48.2 84 50.2 78 C53 72 56 66 55 58.5 Z', true],
    ['M47.6 175 C43.8 195 44.6 220 47.6 240 C49.6 252 51 263 50 277 C47 293 48 312 52.6 332 L54.6 339 L69.4 339 L70.4 330 C72.4 312 74.2 294 72.4 278 C71.4 266 73 254 74.4 240 C76.2 226 78.2 210 80 196 Z'],
    ['M24.2 181 C19.6 182 17.6 188 18.2 195 C18.8 201 21.4 205 24.6 205 C27.8 204.6 29.4 200 29.4 193.6 C29.4 187 28.6 181.6 24.2 181 Z', true],
    ['M54.4 337 C50.4 339 48.6 343.4 49.6 347.4 C51.6 350.4 57.6 350.6 64.6 349.6 C68.6 348.6 70.4 345.6 69.6 341.4 C68.6 338.4 62.4 337 54.4 337 Z'],
  ],
};

export const MIRROR = 'matrix(-1 0 0 1 160 0)';
export const ARM = 'rotate(5 44 66)';

const ABS = (y, h) => `M71 ${y} h8.4 v${h} h-8.4 a2.4 2.4 0 0 1 -2.4 -2.4 v-${h - 4.8} a2.4 2.4 0 0 1 2.4 -2.4 Z`;
const ARM_UP = 'M37.6 97 C33 103 30 112 29.2 122 C29 127 32.6 130.4 36.4 129 C40.6 122.6 43.6 112 44.6 101.6 C44 95.4 40.8 94 37.6 97 Z';
const ARM_LOW = 'M30.2 132.4 C27.2 144 24.4 158 22.2 177.6 C24.2 180.6 27.4 180.8 29.4 178 C32.4 164 36.2 150 39.2 136.4 C36.4 131.2 33.2 130.2 30.2 132.4 Z';
const DELT = 'M53.4 60.4 C44.4 60 36.4 65 34.6 74 C33.6 82 35.4 90 38.2 95.8 C41.2 89 45.2 84 49 80 C52.2 76 55 70.4 55.2 64.4 C55 62.4 54.4 61 53.4 60.4 Z';

// [how it's drawn, body part, muscle group, path]: 'both' is mirrored, 'arm' mirrored and hung like the arm, 'mid' once
const FRONT = [
  ['mid', 'neck', 'neck', BASE.neck],
  ['both', 'back', 'traps', 'M72.4 47.6 C68.4 53 61.4 56.8 55.6 58.8 C61.4 60.8 67.4 61 72.2 58.4 C73.2 55 73.2 51 72.4 47.6 Z'],
  ['both', 'shoulders', 'shoulders', DELT],
  ['both', 'chest', 'chest', 'M79 64 L79 94 C72 98.4 63 99.4 57 96.4 C52 93.4 49.2 88.4 49.2 82.4 C52.2 74.4 57.2 67.4 62.2 63.4 C68.2 61.4 75 61.8 79 64 Z'],
  ['arm', 'arms', 'biceps', ARM_UP],
  ['arm', 'arms', 'forearms', ARM_LOW],
  ['both', 'abs', 'abdominals', ABS(100.5, 15)],
  ['both', 'abs', 'abdominals', ABS(117.5, 15)],
  ['both', 'abs', 'abdominals', ABS(134.5, 15)],
  ['both', 'abs', 'abdominals', 'M71 151.5 h8.4 v22.5 C75.6 174 72 170.6 69.8 165.4 C68.8 161 68.6 157 68.6 154 a2.4 2.4 0 0 1 2.4 -2.5 Z'],
  ['both', 'abs', 'abdominals', 'M66.6 101 C60.8 101 55.8 102 52.2 104 C51.4 118 52.2 134 55 148 C58.8 155 62.6 161 66.8 166.4 C66 152 66 128 66.6 101 Z'],
  ['both', 'legs', 'quadriceps', 'M50.2 178.4 C46.2 194 46.2 216 49.2 236 C51.2 247 54.2 254 58.2 257 L68.4 257 C71.2 247 73 232 73.4 216 C73.8 204 71.2 192.4 66.6 186.4 C61.6 181.4 55.6 178.4 50.2 178.4 Z'],
  ['both', 'legs', 'adductors', 'M77.6 197 C78.6 210 77.6 226 74.8 242 C73.4 232 72.6 218 71.4 204.4 C73.2 200.4 75.2 197.8 77.6 197 Z'],
  ['both', 'legs', 'calves', 'M51 278.6 C47.2 292.6 48 310.6 53.8 330.4 L59.8 330.4 C59.8 314 61.2 296 62.4 280.6 C59.2 276.4 54.2 276 51 278.6 Z'],
  ['both', 'legs', 'calves', 'M64.8 279.4 C66.8 294 67.6 312 67.2 330.4 L70 330.4 C72 312 73 294 71.4 278.6 C69.4 276.4 66.6 276.6 64.8 279.4 Z'],
];

const BACK = [
  ['both', 'back', 'traps', 'M80 44 L73.2 48.8 C66.4 54.8 58.4 58 52.4 60.2 C59.4 64.2 66 70.4 70.6 79.6 C74.6 88.6 77.8 98 80 107 Z'],
  ['both', 'shoulders', 'shoulders', DELT],
  ['both', 'back', 'upper_back', 'M69.4 82.6 C64.4 74.2 58.2 68.8 52.2 68.4 C49.4 76.4 49.4 86.4 52.4 94.4 C58.4 100.4 66.4 104.6 76.4 107 C75 98.6 72.6 90.4 69.4 82.6 Z'],
  ['both', 'back', 'lats', 'M51.8 98.6 C50 108.6 49 120.6 50 132.6 C52 146.6 58 158.6 66 165 C68.4 156 70.6 142 72.2 128 L76.8 110.6 C67.8 108.6 58.8 104.6 51.8 98.6 Z'],
  ['both', 'back', 'lower_back', 'M74.6 116.6 C73.4 132 72.4 150 71.4 167.6 C72.4 172.6 74.6 176.4 79 178.4 L79 112.6 C77 113.6 75.4 114.8 74.6 116.6 Z'],
  ['arm', 'arms', 'triceps', ARM_UP],
  ['arm', 'arms', 'forearms', ARM_LOW],
  ['both', 'glutes', 'glutes', 'M79 180.6 C70 178.6 59 179.4 53 185.4 C48.2 193.4 48.2 206.4 52.2 214.4 C59.2 222.4 71 223.2 79 219.2 Z'],
  ['both', 'legs', 'hamstrings', 'M52 221.4 C50 235.4 51 249.4 56 258.4 L69.4 258.4 C72.2 247.4 74 236.4 75.2 224.6 C67.2 226.4 59 226 52 221.4 Z'],
  ['both', 'legs', 'calves', 'M52.8 276.6 C48.8 288.6 48.8 304.6 52.8 316.6 C56.8 322.6 62.8 322.6 66.8 316.6 C70.8 304.6 71.8 290.6 70 276.4 C64 272.2 58 272.2 52.8 276.6 Z'],
];

const TRANSFORMS = { mid: [''], both: ['', MIRROR], arm: [ARM, `${MIRROR} ${ARM}`] };

const expand = (shapes) =>
  shapes.flatMap(([how, region, muscle, d], index) =>
    TRANSFORMS[how].map((transform, copy) => ({ key: `${index}-${copy}`, region, muscle, d, transform: transform || undefined }))
  );

export const SHAPES = { front: expand(FRONT), back: expand(BACK) };

// Abductors sit at the outer hip, drawn with the glutes
const DRAWN_AS = { abductors: 'glutes' };
export const drawnAs = (muscle) => DRAWN_AS[muscle] || muscle;

// Muscle groups the figure can mark: cardio, "other" and full body have no place of their own
const DRAWN = new Set([...FRONT, ...BACK].map(([, , muscle]) => muscle));
export const isDrawn = (muscle) => DRAWN.has(drawnAs(muscle));
